<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useToggle } from '@vueuse/core';
import { useAlert } from 'dashboard/composables';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import StrategiesList from './StrategiesList.vue';
import StrategyDialog from './StrategyDialog.vue';
import StrategiesEmptyState from './StrategiesEmptyState.vue';
import ConfirmDeleteDialog from './ConfirmDeleteDialog.vue';

const { t } = useI18n();
const store = useStore();

const selectedStrategy = ref(null);
const [showDialog, toggleDialog] = useToggle();
const [isEditing, setIsEditing] = useToggle();
const confirmDeleteRef = ref(null);

const uiFlags = computed(() => store.getters['strategies/getUIFlags']);
const isFetching = computed(() => uiFlags.value.isFetching);
const strategies = computed(() => store.getters['strategies/getStrategies']);
const hasNoStrategies = computed(
  () => strategies.value?.length === 0 && !isFetching.value
);

const fetchStrategies = async () => {
  await store.dispatch('strategies/fetchStrategies');
};

onMounted(() => {
  fetchStrategies();
});

const handleCreate = () => {
  selectedStrategy.value = null;
  setIsEditing(false);
  toggleDialog(true);
};

const handleEdit = strategy => {
  selectedStrategy.value = strategy;
  setIsEditing(true);
  toggleDialog(true);
};

const handleDelete = strategy => {
  selectedStrategy.value = strategy;
  confirmDeleteRef.value?.open();
};

const handleCloseDialog = () => {
  toggleDialog(false);
  selectedStrategy.value = null;
  setIsEditing(false);
};

const handleRefresh = () => {
  fetchStrategies();
};
</script>

<template>
  <div class="flex flex-col h-full bg-n-background">
    <header
      class="flex items-center justify-between px-6 py-4 border-b border-n-weak"
    >
      <div>
        <h1
          class="text-xl font-semibold text-n-slate-12 flex items-center gap-2"
        >
          <span class="i-lucide-settings size-6" />
          {{ t('STRATEGIES.HEADER_TITLE') }}
        </h1>
        <p class="text-sm text-n-slate-11 mt-1">
          {{ t('STRATEGIES.HEADER_SUBTITLE') }}
        </p>
      </div>
      <div class="flex items-center gap-2">
        <Button
          variant="ghost"
          icon="i-lucide-refresh-cw"
          @click="handleRefresh"
        >
          {{ t('STRATEGIES.REFRESH') }}
        </Button>
        <Button
          icon="i-lucide-plus"
          color-scheme="primary"
          @click="handleCreate"
        >
          {{ t('STRATEGIES.NEW_STRATEGY') }}
        </Button>
      </div>
    </header>

    <div class="flex-1 overflow-auto p-6">
      <div
        v-if="isFetching"
        class="flex items-center justify-center py-10 text-n-slate-11"
      >
        <Spinner />
      </div>

      <StrategiesList
        v-else-if="!hasNoStrategies"
        :strategies="strategies"
        @edit="handleEdit"
        @delete="handleDelete"
      />

      <StrategiesEmptyState v-else @create="handleCreate" />
    </div>

    <StrategyDialog
      v-if="showDialog"
      :strategy="selectedStrategy"
      :is-editing="isEditing"
      @close="handleCloseDialog"
    />

    <ConfirmDeleteDialog ref="confirmDeleteRef" :strategy="selectedStrategy" />
  </div>
</template>
